import './style/global.less';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
// Polyfill for React 17 compatibility with AI SDK
import { useSyncExternalStore } from 'use-sync-external-store/shim';
// @ts-ignore - Add useSyncExternalStore to React for AI SDK compatibility
if (!React.useSyncExternalStore) {
  // @ts-ignore
  React.useSyncExternalStore = useSyncExternalStore;
}
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { ConfigProvider } from '@arco-design/web-react';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
// 暂时注释掉 HelmetProvider 以解决类型错误
// import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import rootReducer from './store';
import PageLayout from './layout';
import { GlobalContext } from './context';
import Login from './pages/login';
import TermsOfService from './pages/terms-of-service';
import PrivacyPolicy from './pages/privacy-policy';
import ChangeLog from './pages/change-log';
import checkLogin, { verifyToken, isLoggedIn } from '@/utils/checkLogin';
import changeTheme from './utils/changeTheme';
import useStorage from './utils/useStorage';
import ProtectedRoute from './components/ProtectedRoute';
import './mock';

const store = createStore(rootReducer);

function Index() {
  const [lang, setLang] = useStorage('arco-lang', 'zh-CN');
  const [theme, setTheme] = useStorage('arco-theme', 'light');

  function getArcoLocale() {
    switch (lang) {
      case 'zh-CN':
        return zhCN;
      case 'en-US':
        return enUS;
      default:
        return zhCN;
    }
  }

  function fetchUserInfo() {
    store.dispatch({
      type: 'update-userInfo',
      payload: { userLoading: true },
    });
    axios.get('/api/user/userInfo').then((res) => {
      store.dispatch({
        type: 'update-userInfo',
        payload: { userInfo: res.data, userLoading: false },
      });
    });
  }

  useEffect(() => {
    // 防止循环重定向，如果当前已经在登录页，不再验证
    if (
      window.location.pathname === '/login' ||
      window.location.pathname === '/terms-of-service' ||
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/change-log'
    ) {
      return;
    }

    // 如果已经开始登录流程，不执行验证
    if (sessionStorage.getItem('login_in_progress')) {
      return;
    }

    const validateToken = async () => {
      try {
        console.log('开始验证令牌并获取用户信息');
        const { valid, user } = await verifyToken();
        console.log('令牌验证结果:', valid, '用户信息:', user);

        if (valid) {
          // 确保用户信息完整
          const userInfo = {
            // 先保留用户基本信息
            id: user.id,
            username: user.username,
            nickname: user.nickname,
            email: user.email,
            phone: user.phone,
            avatarUrl: user.avatarUrl,
            createdAt: user.createdAt,

            // 添加会员等级信息
            currentLevel: user.currentLevel,
            points: user.points,

            // 设置默认值，确保即使后端未返回这些字段也不会出现undefined
            name: user.name || user.username || user.nickname || '未命名用户',
            avatar: user.avatarUrl || user.avatar || '',
            accountId: user.accountId || user.id || '未设置',
            phoneNumber: user.phoneNumber || user.phone || '未设置',
            registrationTime: user.registrationTime || user.createdAt || '未知',
          };

          console.log('处理后的基本用户信息:', userInfo);

          // 将权限作为单独的dispatch发送，避免覆盖用户信息
          // 更新用户权限
          if (user.permissions) {
            console.log('更新用户权限:', user.permissions);
            store.dispatch({
              type: 'update-permissions',
              payload: { permissions: user.permissions },
            });
          }

          // 更新用户基本信息
          store.dispatch({
            type: 'update-userInfo',
            payload: { userInfo, userLoading: false },
          });
        } else {
          // 设置重定向标记，防止循环
          sessionStorage.setItem('login_in_progress', 'true');

          // 无效token，重定向到登录页
          const redirectPath = encodeURIComponent(window.location.pathname);
          const redirectSearch = window.location.search
            ? encodeURIComponent(window.location.search)
            : '';
          window.location.href = `/login?redirect=${redirectPath}${redirectSearch}`;
        }
      } catch (error) {
        console.error('Token验证失败:', error);
        // 设置重定向标记，防止循环
        sessionStorage.setItem('login_in_progress', 'true');

        // 重定向到登录页
        const redirectPath = encodeURIComponent(window.location.pathname);
        const redirectSearch = window.location.search
          ? encodeURIComponent(window.location.search)
          : '';
        window.location.href = `/login?redirect=${redirectPath}${redirectSearch}`;
      }
    };

    validateToken();
  }, []);

  useEffect(() => {
    changeTheme(theme);
  }, [theme]);

  const contextValue = {
    lang,
    setLang,
    theme,
    setTheme,
  };

  return (
    <BrowserRouter>
      {/* 暂时移除 HelmetProvider */}
      <ConfigProvider
        locale={getArcoLocale()}
        componentConfig={{
          Card: {
            bordered: false,
          },
          List: {
            bordered: false,
          },
          Table: {
            border: false,
          },
        }}
      >
        <Provider store={store}>
          <GlobalContext.Provider value={contextValue}>
            <Switch>
              <Route path="/login" component={Login} />
              <Route path="/terms-of-service" component={TermsOfService} />
              <Route path="/privacy-policy" component={PrivacyPolicy} />
              <Route path="/change-log" component={ChangeLog} />
              <ProtectedRoute path="/" component={PageLayout} />
            </Switch>
          </GlobalContext.Provider>
        </Provider>
      </ConfigProvider>
    </BrowserRouter>
  );
}

ReactDOM.render(<Index />, document.getElementById('root'));
